import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Card,
  Title2,
  Body1,
  Text,
  Button,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  Divider,
} from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  PersonRegular,
  CalendarRegular,
  BuildingRegular,
  MailRegular,
  LaptopRegular,
  KeyRegular,
  DocumentRegular,
  CheckmarkRegular,
  ErrorCircleRegular,
  PeopleTeamRegular,
} from '@fluentui/react-icons';
import { apiClient } from '../api/client';
import { DATETIME_FORMAT, DATE_FORMAT, ROUTES } from '../config/constants';

const useStyles = makeStyles({
  container: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  backButton: {
    marginBottom: tokens.spacingVerticalL,
  },
  headerCard: {
    padding: tokens.spacingVerticalL,
    marginBottom: tokens.spacingVerticalL,
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  headerBadges: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  headerMeta: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalS,
    flexWrap: 'wrap',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  passwordRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  detailsCard: {
    padding: tokens.spacingVerticalL,
    marginBottom: tokens.spacingVerticalL,
  },
  section: {
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalM,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: tokens.spacingVerticalS,
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  detailLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  detailValue: {
    fontSize: tokens.fontSizeBase300,
  },
  badgeList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  accessBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  notesBox: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    marginTop: tokens.spacingVerticalXS,
  },
  hardwareList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  timelineCard: {
    padding: tokens.spacingVerticalL,
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
    paddingLeft: tokens.spacingHorizontalM,
  },
  timelineEntry: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    position: 'relative',
    paddingBottom: tokens.spacingVerticalL,
  },
  timelineDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
    marginTop: '4px',
    position: 'relative',
    zIndex: 1,
  },
  timelineLine: {
    position: 'absolute',
    left: '5px',
    top: '16px',
    bottom: '0',
    width: '2px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
  timelineContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  timelineAction: {
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'capitalize',
  },
  timelineMeta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  timelineNotes: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    marginTop: '2px',
    fontStyle: 'italic',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  errorList: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    marginTop: tokens.spacingVerticalXS,
    color: tokens.colorPaletteRedForeground1,
  },
});

interface HistoryEntry {
  _id: string;
  requestId: string;
  requestType: 'onboarding' | 'offboarding' | 'teams_site' | 'employee_update' | 'hardware';
  action: string;
  actorName: string;
  actorEmail: string;
  timestamp: string;
  notes?: string;
  errorDetails?: string;
  requestSnapshot: Record<string, unknown>;
}

interface PaginatedResponse {
  data: HistoryEntry[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

const ACTION_COLORS: Record<string, string> = {
  completed: tokens.colorPaletteGreenBackground3,
  approved: tokens.colorPaletteGreenBackground3,
  created: tokens.colorPaletteBlueBorderActive,
  denied: tokens.colorPaletteRedBackground3,
  failed: tokens.colorPaletteRedBackground3,
  auto_disabled: tokens.colorPaletteYellowBackground3,
  updated: tokens.colorPaletteBlueBorderActive,
};

function getStatusBadgeColor(action: string): 'success' | 'danger' | 'warning' | 'informative' | 'subtle' {
  switch (action) {
    case 'completed':
    case 'approved':
      return 'success';
    case 'denied':
    case 'failed':
      return 'danger';
    case 'created':
      return 'informative';
    case 'auto_disabled':
      return 'warning';
    default:
      return 'subtle';
  }
}

function getTypeBadge(type: string) {
  switch (type) {
    case 'onboarding':
      return <Badge color="brand">Onboarding</Badge>;
    case 'offboarding':
      return <Badge color="danger">Offboarding</Badge>;
    case 'teams_site':
      return <Badge color="informative">Teams</Badge>;
    case 'employee_update':
      return <Badge color="warning">Profile Update</Badge>;
    case 'hardware':
      return <Badge color="informative">Hardware Request</Badge>;
    default:
      return <Badge>{type}</Badge>;
  }
}

function getUserName(snapshot: Record<string, unknown>): string {
  if (snapshot.displayName) return snapshot.displayName as string;
  if (snapshot.userDisplayName) return snapshot.userDisplayName as string;
  if (snapshot.teamName) return snapshot.teamName as string;
  if (snapshot.firstName && snapshot.lastName) {
    return `${snapshot.firstName} ${snapshot.lastName}`;
  }
  return 'Unknown';
}

function DetailItem({ label, value }: { label: string; value?: string | number | null }) {
  const styles = useStyles();
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className={styles.detailItem}>
      <Text className={styles.detailLabel}>{label}</Text>
      <Text className={styles.detailValue}>{String(value)}</Text>
    </div>
  );
}

export function HistoryDetailPage() {
  const styles = useStyles();
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!requestId) {
      setLoading(false);
      setError('No request ID provided.');
      return;
    }
    setLoading(true);
    setError(null);
    apiClient
      .get<{ data: PaginatedResponse }>(`/requests/history?requestId=${encodeURIComponent(requestId)}&limit=100&sort=-timestamp`)
      .then((response) => {
        setEntries(response.data.data);
        if (response.data.data.length === 0) {
          setError('No history found for this request.');
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load request details');
      })
      .finally(() => setLoading(false));
  }, [requestId]);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', DATE_FORMAT);

  const formatDateTime = (dateString: string) =>
    new Date(dateString).toLocaleString('en-US', DATETIME_FORMAT);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <Spinner size="large" label="Loading request details..." />
        </div>
      </div>
    );
  }

  if (error || entries.length === 0) {
    return (
      <div className={styles.container}>
        <Button
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          className={styles.backButton}
          onClick={() => navigate(ROUTES.HISTORY)}
        >
          Back to History
        </Button>
        <MessageBar intent="error">
          <MessageBarBody>{error || 'Request not found.'}</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  // Latest entry (newest) has the most current snapshot
  const latest = entries[0];
  const original = entries[entries.length - 1];
  const snapshot = latest.requestSnapshot;
  const requestType = latest.requestType;

  // Find the completed entry for password (may not be the latest if updated/failed after)
  const completedEntry = entries.find(
    (e) => e.requestType === 'onboarding' && e.action === 'completed'
  );
  const password = completedEntry
    ? (completedEntry.requestSnapshot.temporaryPassword as string) ||
      (completedEntry.requestSnapshot.createdUserPassword as string)
    : null;

  return (
    <div className={styles.container}>
      <Button
        appearance="subtle"
        icon={<ArrowLeftRegular />}
        className={styles.backButton}
        onClick={() => navigate(ROUTES.HISTORY)}
      >
        Back to History
      </Button>

      {/* Header Card */}
      <Card className={styles.headerCard}>
        <div className={styles.headerTop}>
          <Title2>{getUserName(snapshot)}</Title2>
          <div className={styles.headerBadges}>
            {getTypeBadge(requestType)}
            <Badge color={getStatusBadgeColor(latest.action)} size="large">
              {latest.action.replace(/_/g, ' ')}
            </Badge>
          </div>
        </div>
        <Body1 style={{ color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalXS }}>
          Request ID: {requestId}
        </Body1>
        {password && (
          <div className={styles.passwordRow}>
            <KeyRegular />
            Password: {password}
          </div>
        )}
        <div className={styles.headerMeta}>
          <span className={styles.metaItem}>
            <PersonRegular /> Submitted by: {original.actorName}
          </span>
          <span className={styles.metaItem}>
            <CalendarRegular /> {formatDateTime(original.timestamp)}
          </span>
        </div>
      </Card>

      {/* Request Details Card */}
      <Card className={styles.detailsCard}>
        <Title2 style={{ marginBottom: tokens.spacingVerticalM }}>Request Details</Title2>
        {requestType === 'onboarding' && renderOnboardingDetails(snapshot)}
        {requestType === 'offboarding' && renderOffboardingDetails(snapshot)}
        {requestType === 'hardware' && renderHardwareDetails(snapshot)}
        {requestType === 'employee_update' && renderEmployeeUpdateDetails(snapshot)}
        {requestType === 'teams_site' && renderTeamsSiteDetails(snapshot)}
      </Card>

      {/* Activity Timeline */}
      <Card className={styles.timelineCard}>
        <Title2 style={{ marginBottom: tokens.spacingVerticalL }}>Activity Timeline</Title2>
        <div className={styles.timeline}>
          {entries.map((entry, index) => {
            const isLast = index === entries.length - 1;
            const dotColor = ACTION_COLORS[entry.action] || tokens.colorNeutralStroke1;
            return (
              <div key={entry._id} className={styles.timelineEntry}>
                <div>
                  <div className={styles.timelineDot} style={{ backgroundColor: dotColor }} />
                  {!isLast && <div className={styles.timelineLine} />}
                </div>
                <div className={styles.timelineContent}>
                  <Text className={styles.timelineAction}>
                    {entry.action.replace(/_/g, ' ')}
                  </Text>
                  <Text className={styles.timelineMeta}>
                    by {entry.actorName} &middot; {formatDateTime(entry.timestamp)}
                  </Text>
                  {entry.notes && (
                    <Text className={styles.timelineNotes}>{entry.notes}</Text>
                  )}
                  {entry.errorDetails && (
                    <Text className={styles.timelineNotes} style={{ color: tokens.colorPaletteRedForeground1 }}>
                      Error: {entry.errorDetails}
                    </Text>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );

  // ─── Type-Specific Renderers ──────────────────────────────────

  function renderOnboardingDetails(s: Record<string, unknown>) {
    const accessItems: string[] = [];
    const access = s.accessProvisioning as Record<string, boolean> | undefined;
    if (access) {
      if (access.vpn) accessItems.push('VPN');
      if (access.hrSystem) accessItems.push('HR System');
      if (access.hubSpot) accessItems.push('HubSpot');
      if (access.fileServer) accessItems.push('File Server');
      if (access.sage) accessItems.push('Sage');
    }
    const hw = s.hardware as Record<string, unknown> | undefined;
    const hwPeripherals: string[] = [];
    if (hw) {
      if (hw.primaryDisplay) hwPeripherals.push('Primary Display');
      if (hw.secondaryDisplay) hwPeripherals.push('Secondary Display');
      if (hw.mouse) hwPeripherals.push('Mouse');
      if (hw.keyboard) hwPeripherals.push('Keyboard');
      if (hw.laptopBag) hwPeripherals.push('Laptop Bag');
    }
    const additionalSoftware = s.additionalSoftware as string[] | undefined;

    // Provisioning results from completed snapshot
    const upn = s.createdUserPrincipalName as string | undefined;
    const errors = Array.isArray(s.provisioningErrors) ? s.provisioningErrors as string[] : undefined;

    return (
      <>
        {/* Employee Details */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <PersonRegular />
            <Text weight="semibold">Employee Details</Text>
          </div>
          <div className={styles.detailsGrid}>
            <DetailItem label="First Name" value={s.firstName as string} />
            <DetailItem label="Last Name" value={s.lastName as string} />
            <DetailItem label="Display Name" value={s.displayName as string} />
            <DetailItem label="Job Title" value={s.jobTitle as string} />
            <DetailItem label="Department" value={s.department as string} />
            <DetailItem label="Employment Type" value={s.role as string} />
            <DetailItem label="Site Code" value={s.siteCode as string} />
            <DetailItem label="Mobile Phone" value={s.mobilePhone as string} />
            <DetailItem label="Business Phone" value={s.businessPhone as string} />
          </div>
        </div>

        <Divider />

        {/* Dates */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <CalendarRegular />
            <Text weight="semibold">Dates</Text>
          </div>
          <div className={styles.detailsGrid}>
            <DetailItem label="Start Date" value={s.startDate ? formatDate(s.startDate as string) : undefined} />
            {s.endDate && <DetailItem label="End Date (Contract)" value={formatDate(s.endDate as string)} />}
          </div>
        </div>

        <Divider />

        {/* Line Manager */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <BuildingRegular />
            <Text weight="semibold">Line Manager</Text>
          </div>
          <div className={styles.detailsGrid}>
            <DetailItem label="Name" value={s.lineManagerName as string} />
            <DetailItem label="Email" value={s.lineManagerEmail as string} />
          </div>
        </div>

        <Divider />

        {/* Access Provisioning */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <KeyRegular />
            <Text weight="semibold">Access Provisioning</Text>
          </div>
          {accessItems.length > 0 ? (
            <div className={styles.badgeList}>
              {accessItems.map((item) => (
                <Badge key={item} appearance="tint" color="success">
                  <span className={styles.accessBadge}>
                    <CheckmarkRegular /> {item}
                  </span>
                </Badge>
              ))}
            </div>
          ) : (
            <Text style={{ color: tokens.colorNeutralForeground3 }}>No additional access requested</Text>
          )}
        </div>

        {/* Additional Software */}
        {additionalSoftware && additionalSoftware.length > 0 && (
          <>
            <Divider />
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <DocumentRegular />
                <Text weight="semibold">Additional Software</Text>
              </div>
              <div className={styles.badgeList}>
                {additionalSoftware.map((sw) => (
                  <Badge key={sw} appearance="outline">{sw}</Badge>
                ))}
              </div>
            </div>
          </>
        )}

        <Divider />

        {/* Hardware */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <LaptopRegular />
            <Text weight="semibold">Hardware</Text>
          </div>
          {hw && (
            <div className={styles.detailsGrid}>
              <DetailItem
                label="Device"
                value={
                  hw.isReused
                    ? `Reused${hw.serviceTag ? ` (${hw.serviceTag})` : ''}`
                    : `New ${(hw.type as string) || 'Device'}`
                }
              />
            </div>
          )}
          {hwPeripherals.length > 0 && (
            <div className={styles.hardwareList} style={{ marginTop: tokens.spacingVerticalS }}>
              {hwPeripherals.map((item) => (
                <Badge key={item} appearance="outline" color="informative">{item}</Badge>
              ))}
            </div>
          )}
          {!hw && hwPeripherals.length === 0 && (
            <Text style={{ color: tokens.colorNeutralForeground3 }}>No hardware specified</Text>
          )}
        </div>

        {/* Additional Requests */}
        {s.additionalRequests && (
          <>
            <Divider />
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <DocumentRegular />
                <Text weight="semibold">Additional Requests</Text>
              </div>
              <div className={styles.notesBox}>
                <Text>{s.additionalRequests as string}</Text>
              </div>
            </div>
          </>
        )}

        {/* Provisioning Results */}
        {(upn || errors) && (
          <>
            <Divider />
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <CheckmarkRegular />
                <Text weight="semibold">Provisioning Results</Text>
              </div>
              <div className={styles.detailsGrid}>
                <DetailItem label="User Principal Name" value={upn} />
              </div>
              {errors && errors.length > 0 && (
                <div className={styles.errorList}>
                  {errors.map((err, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <ErrorCircleRegular /> {err}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </>
    );
  }

  function renderOffboardingDetails(s: Record<string, unknown>) {
    const errors = Array.isArray(s.provisioningErrors) ? s.provisioningErrors as string[] : undefined;

    return (
      <>
        {/* Employee Details */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <PersonRegular />
            <Text weight="semibold">Employee Details</Text>
          </div>
          <div className={styles.detailsGrid}>
            <DetailItem label="Display Name" value={s.userDisplayName as string} />
            <DetailItem label="Email" value={s.userEmail as string} />
            <DetailItem label="Principal Name" value={s.userPrincipalName as string} />
            <DetailItem label="Department" value={s.department as string} />
            <DetailItem label="Job Title" value={s.jobTitle as string} />
          </div>
        </div>

        <Divider />

        {/* Dates */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <CalendarRegular />
            <Text weight="semibold">Dates</Text>
          </div>
          <div className={styles.detailsGrid}>
            <DetailItem label="Termination Date" value={s.terminationDate ? formatDate(s.terminationDate as string) : undefined} />
            {s.lastWorkingDay && <DetailItem label="Last Working Day" value={formatDate(s.lastWorkingDay as string)} />}
          </div>
        </div>

        <Divider />

        {/* Data Handling */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <MailRegular />
            <Text weight="semibold">Data Handling</Text>
          </div>
          <div className={styles.detailsGrid}>
            <DetailItem
              label="Mailbox Action"
              value={s.removeMailbox ? 'Remove mailbox' : 'Keep mailbox'}
            />
            <DetailItem label="Forward Mail To" value={s.mailForwardingAddress as string} />
          </div>
          {s.oneDriveDelegateName && (
            <div className={styles.detailsGrid} style={{ marginTop: tokens.spacingVerticalS }}>
              <DetailItem label="OneDrive Delegate" value={s.oneDriveDelegateName as string} />
              <DetailItem label="Delegate Email" value={s.oneDriveDelegateEmail as string} />
            </div>
          )}
        </div>

        {/* Additional Notes */}
        {s.additionalNotes && (
          <>
            <Divider />
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <DocumentRegular />
                <Text weight="semibold">Additional Notes</Text>
              </div>
              <div className={styles.notesBox}>
                <Text>{s.additionalNotes as string}</Text>
              </div>
            </div>
          </>
        )}

        {/* Results */}
        {(s.accountDisabled || s.licensesRemoved || s.groupsRemoved || errors) && (
          <>
            <Divider />
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <CheckmarkRegular />
                <Text weight="semibold">Offboarding Results</Text>
              </div>
              <div className={styles.badgeList}>
                {s.accountDisabled && (
                  <Badge appearance="tint" color="success">
                    <span className={styles.accessBadge}><CheckmarkRegular /> Account Disabled</span>
                  </Badge>
                )}
                {s.licensesRemoved && (
                  <Badge appearance="tint" color="success">
                    <span className={styles.accessBadge}><CheckmarkRegular /> Licenses Removed</span>
                  </Badge>
                )}
                {s.groupsRemoved && (
                  <Badge appearance="tint" color="success">
                    <span className={styles.accessBadge}><CheckmarkRegular /> Groups Removed</span>
                  </Badge>
                )}
              </div>
              {errors && errors.length > 0 && (
                <div className={styles.errorList}>
                  {errors.map((err, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <ErrorCircleRegular /> {err}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </>
    );
  }

  function renderHardwareDetails(s: Record<string, unknown>) {
    const mainHardware = s.mainHardware as string[] | undefined;
    const peripherals = s.peripherals as string[] | undefined;
    const monitorQuantity = s.monitorQuantity as number | undefined;

    return (
      <>
        {/* Beneficiary Details */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <PersonRegular />
            <Text weight="semibold">Beneficiary Details</Text>
          </div>
          <div className={styles.detailsGrid}>
            <DetailItem label="Employee Name" value={s.userDisplayName as string} />
            <DetailItem label="Email" value={s.userEmail as string} />
            <DetailItem label="User Principal Name" value={s.userPrincipalName as string} />
            <DetailItem label="Line Manager" value={s.managerName as string} />
          </div>
        </div>

        <Divider />

        {/* Requested Hardware */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <LaptopRegular />
            <Text weight="semibold">Requested Hardware</Text>
          </div>

          {mainHardware && mainHardware.length > 0 && (
            <div style={{ marginBottom: tokens.spacingVerticalS }}>
              <Text className={styles.detailLabel}>Main Hardware:</Text>
              <div className={styles.hardwareList}>
                {mainHardware.map((item) => (
                  <Badge key={item} appearance="filled" color="brand">{item}</Badge>
                ))}
              </div>
            </div>
          )}

          {peripherals && peripherals.length > 0 && (
            <div style={{ marginBottom: tokens.spacingVerticalS }}>
              <Text className={styles.detailLabel}>Peripherals:</Text>
              <div className={styles.hardwareList}>
                {peripherals.map((item) => (
                  <Badge key={item} appearance="filled" color="informative">{item}</Badge>
                ))}
              </div>
            </div>
          )}

          {monitorQuantity !== undefined && monitorQuantity > 0 && (
            <div>
              <Text className={styles.detailLabel}>Monitors:</Text>
              <Text className={styles.detailValue}>
                {monitorQuantity} monitor{monitorQuantity > 1 ? 's' : ''}
              </Text>
            </div>
          )}
        </div>

        {/* Justification */}
        {s.justification && (
          <>
            <Divider />
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <DocumentRegular />
                <Text weight="semibold">Business Justification</Text>
              </div>
              <div className={styles.notesBox}>
                <Text>{s.justification as string}</Text>
              </div>
            </div>
          </>
        )}

        {/* Fulfillment Info */}
        {(s.trackingNumber || s.deliveryDate || s.fulfillmentNotes) && (
          <>
            <Divider />
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <CheckmarkRegular />
                <Text weight="semibold">Fulfillment</Text>
              </div>
              <div className={styles.detailsGrid}>
                <DetailItem label="Tracking Number" value={s.trackingNumber as string} />
                <DetailItem label="Delivery Date" value={s.deliveryDate ? formatDate(s.deliveryDate as string) : undefined} />
              </div>
              {s.fulfillmentNotes && (
                <div className={styles.notesBox}>
                  <Text>{s.fulfillmentNotes as string}</Text>
                </div>
              )}
            </div>
          </>
        )}
      </>
    );
  }

  function renderEmployeeUpdateDetails(s: Record<string, unknown>) {
    const changes = s.changes as Record<string, { from?: string; to?: string }> | undefined;

    return (
      <>
        {/* Employee Details */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <PersonRegular />
            <Text weight="semibold">Employee Details</Text>
          </div>
          <div className={styles.detailsGrid}>
            <DetailItem label="Display Name" value={s.userDisplayName as string || s.displayName as string} />
            <DetailItem label="Email" value={s.userEmail as string} />
            <DetailItem label="Principal Name" value={s.userPrincipalName as string} />
          </div>
        </div>

        {/* Changes Made */}
        {changes && Object.keys(changes).length > 0 && (
          <>
            <Divider />
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <DocumentRegular />
                <Text weight="semibold">Changes Made</Text>
              </div>
              <div className={styles.detailsGrid}>
                {Object.entries(changes).map(([field, change]) => (
                  <div key={field} className={styles.detailItem}>
                    <Text className={styles.detailLabel} style={{ textTransform: 'capitalize' }}>
                      {field.replace(/([A-Z])/g, ' $1').trim()}
                    </Text>
                    <Text className={styles.detailValue}>
                      {change.from && (
                        <span style={{ textDecoration: 'line-through', color: tokens.colorNeutralForeground3, marginRight: '8px' }}>
                          {change.from}
                        </span>
                      )}
                      <span style={{ color: tokens.colorBrandForeground1 }}>
                        {change.to || '-'}
                      </span>
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Errors */}
        {Array.isArray(s.errors) && s.errors.length > 0 && (
          <>
            <Divider />
            <div className={styles.section}>
              <div className={styles.errorList}>
                {(s.errors as string[]).map((err, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <ErrorCircleRegular /> {err}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  function renderTeamsSiteDetails(s: Record<string, unknown>) {
    const members = s.members as string[] | undefined;

    return (
      <>
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <PeopleTeamRegular />
            <Text weight="semibold">Team Details</Text>
          </div>
          <div className={styles.detailsGrid}>
            <DetailItem label="Team Name" value={s.teamName as string} />
            <DetailItem label="Description" value={s.description as string} />
            <DetailItem label="Visibility" value={s.visibility as string} />
            <DetailItem label="Owner" value={s.ownerName as string || s.ownerEmail as string} />
          </div>
        </div>

        {members && members.length > 0 && (
          <>
            <Divider />
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <PersonRegular />
                <Text weight="semibold">Members</Text>
              </div>
              <div className={styles.badgeList}>
                {members.map((member) => (
                  <Badge key={member} appearance="outline">{member}</Badge>
                ))}
              </div>
            </div>
          </>
        )}
      </>
    );
  }
}
